import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, AlertCircle } from "lucide-react";
import { authApi, formatError } from "../../lib/adminApi";
import { Logo } from "../../components/Logo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@channelpartner.network");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi.login(email.trim(), password, otp.trim());
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-5">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-strong rounded-3xl p-6 md:p-8 w-full max-w-md flex flex-col gap-5"
        data-testid="admin-login-form"
      >
        <div className="flex flex-col items-start gap-3">
          <Logo className="h-9" />
          <div className="w-10 h-10 rounded-2xl bg-[#FFF3ED] text-[#FF5E1A] flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A]">
            Admin sign in
          </h1>
          <p className="text-sm text-[#475569]">
            Manage the 100 pain points and view swipe analytics.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#0F172A]">Email</label>
          <input
            data-testid="admin-email-input"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-2xl glass-input focus:outline-none focus:border-[#FF5E1A] focus:ring-2 focus:ring-[#FF5E1A]/20 text-base text-[#0F172A]"
            required
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#0F172A]">
            Password
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-2xl glass-input focus:outline-none focus:border-[#FF5E1A] focus:ring-2 focus:ring-[#FF5E1A]/20 text-base text-[#0F172A]"
            required
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#0F172A]">
            OTP
          </label>
          <input
            data-testid="admin-otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="6-digit code"
            className="w-full px-4 py-3 rounded-2xl glass-input focus:outline-none focus:border-[#FF5E1A] focus:ring-2 focus:ring-[#FF5E1A]/20 text-base text-[#0F172A] tracking-widest"
            required
          />
        </div>

        {error && (
          <div
            className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5"
            data-testid="admin-login-error"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className={`w-full rounded-2xl py-3.5 text-base font-semibold inline-flex items-center justify-center gap-2 transition-all ${
            loading
              ? "bg-slate-300 text-slate-500 cursor-not-allowed"
              : "bg-[#FF5E1A] hover:bg-[#E04F00] text-white cp-shadow-orange active:scale-[0.99]"
          }`}
        >
          {loading ? "Signing in…" : "Sign in"}
          {!loading && <ArrowRight className="w-5 h-5" />}
        </button>
      </motion.form>
    </div>
  );
}
